import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { Box, Paper, CircularProgress, Grid, Typography, Divider, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useMutation from "../../../../shared/components/hooks/useMutation";
import { getLocalStorageString } from "../../../../shared/utils/localStorage";
import NoDataFound from "../../../../shared/components/noDataFoundBanner";
import { PropertyContext } from "../../../../contexts/PropertyContext";
import { PropertyOption } from "../../../../shared/interfaces/property";
import dayjs from "dayjs";
import { formatKpiValue, getKpiConfig } from "../../../../shared/utils/prediction-utils";
import { PredictionBarChart } from "./PredictionBarChart";
import axios from "axios";

interface PredictionGraphProps {
  selectedProperties: PropertyOption[];
  date: Date | null;
  forecastType: string;
  explanationType: string;
  selectedKpis: string[];
  onDataLoaded: (loaded: boolean) => void;
  eventContext?: { name: string; date: string; endDate?: string } | null;
}

export const PredictionGraph = ({
  selectedProperties,
  date,
  explanationType,
  forecastType,
  selectedKpis,
  onDataLoaded,
  eventContext
}: PredictionGraphProps) => {
  const { modifyData, loading, error } = useMutation();
  const { pmsList } = useContext(PropertyContext);
  const [graphData, setGraphData] = useState<any>(null);

  const [isInternalLoading, setIsInternalLoading] = useState(true);
  const [liveThinkingMessage, setLiveThinkingMessage] = useState("Initializing forecast...");
  const abortControllerRef = useRef<AbortController | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const formattedProperties = useMemo(() =>
    selectedProperties.map(p => `${p.code}:${p.name}`),
    [selectedProperties]);

  const storedUserId = getLocalStorageString("userID") || "unknown_user";
  const storedCustomerId = getLocalStorageString("customerID")?.replace(/"/g, "") || "2";

  const payload = useMemo(() => ({
    user_id: storedUserId,
    customer_id: storedCustomerId,
    selected_properties: formattedProperties,
    explanation_type: explanationType,
    date: date ? dayjs(date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
    kpis: selectedKpis.length > 0 ? selectedKpis : ["occupancy", "adr"],
    forecast_type: forecastType,
    user_query: null,
    pms_list: Array.isArray(pmsList) ? pmsList : [],
    event_name: eventContext?.name || null,
    event_start_date: eventContext?.date || null,
    event_end_date: eventContext?.endDate || null,
  }), [
    formattedProperties, explanationType, date,
    selectedKpis, forecastType, pmsList,
    eventContext, storedUserId, storedCustomerId
  ]);

  useEffect(() => {
    let isMounted = true;

    const fetchGraphData = async () => {
      if (!selectedProperties?.length) return;

      setIsInternalLoading(true);
      setLiveThinkingMessage("Initializing forecast...");
      abortControllerRef.current = new AbortController();

      try {
        const startData = await modifyData(
          `${process.env.REACT_APP_API_URL}/intelligence/forecast/stream-chat`,
          "post",
          payload,
          false,
          abortControllerRef.current.signal
        );

        if (!isMounted) return;

        if (startData?.from_cache === true || !startData?.job_id) {
          const finalResults = startData?.results || startData;
          setGraphData(finalResults || null);
          onDataLoaded(true);
          setIsInternalLoading(false);
          return;
        }

        const sseUrl = `${process.env.REACT_APP_API_URL}/intelligence/forecast/stream/${startData.job_id}`;
        const es = new EventSource(sseUrl);
        eventSourceRef.current = es;

        const closeStream = () => {
          es.close();
          eventSourceRef.current = null;
          if (isMounted) setIsInternalLoading(false);
        };

        es.addEventListener("close", () => closeStream());

        es.addEventListener("message", (event: any) => {
          if (event.data === "[DONE]") {
            closeStream();
            return;
          }

          try {
            const streamData = JSON.parse(event.data);

            if (streamData.status && streamData.status !== "done" && streamData.message) {
              if (isMounted) setLiveThinkingMessage(streamData.message);
            }

            const finalPayload = streamData.result || streamData;

            if (streamData.status === "done" || finalPayload.results !== undefined) {
              if (isMounted) {
                setGraphData(finalPayload.results || finalPayload);
                onDataLoaded(true);
              }
              closeStream();
            }
          } catch (e) {
          }
        });

        es.onerror = (err) => {
          console.error("Graph SSE Error:", err);
          if (isMounted) {
            setGraphData(null);
            onDataLoaded(true);
          }
          closeStream();
        };

      } catch (err: any) {
        if (!axios.isCancel(err)) {
          console.error("Graph API Error", err);
          if (isMounted) {
            setGraphData(null);
            onDataLoaded(true);
          }
        }
        if (isMounted) setIsInternalLoading(false);
      }
    };

    fetchGraphData();

    return () => {
      isMounted = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, [payload, onDataLoaded, selectedProperties?.length]);

  if (loading || isInternalLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, borderRadius: 2, mt: 2, minHeight: '300px', flexDirection: 'column' }}>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          backgroundColor: "#f5f7f9",
          px: 3,
          py: 2,
          borderRadius: "16px",
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
        }}>
          <CircularProgress size={20} />
          <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", fontWeight: 500 }}>
            {liveThinkingMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !graphData || Object.keys(graphData).length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4} mt={2} bgcolor="white" borderRadius={2}>
        <NoDataFound />
      </Box>
    );
  }

  const isIndividualMode = Object.keys(graphData).every(key => !isNaN(Number(key)));

  if (isIndividualMode) {
    return (
      <Box sx={{ mt: 2, mb: 4 }}>
        {Object.keys(graphData).map((propertyId) => {
          const propertyResult = graphData[propertyId];
          const propertyName = propertyResult.property_name || `Property ${propertyId}`;
          const kpiResults = propertyResult.results || {};

          return (
            <Box key={propertyId}
              sx={{
                mb: 2,
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
              <Accordion
                defaultExpanded
                elevation={0}
                disableGutters
                sx={{
                  '&:before': { display: 'none' },
                  borderRadius: 0,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: '#f5f5f5',
                    flexDirection: 'row',
                    '&.Mui-expanded': {
                      minHeight: 48,
                      borderBottom: '1px solid #e0e0e0'
                    }
                  }}
                >
                  <Typography variant="h6" fontSize={18} fontWeight="bold">
                    {propertyName}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {Object.keys(kpiResults).map((kpiKey) => {
                      const kpiData = kpiResults[kpiKey];
                      const hasPredictions = kpiData?.predictions?.length > 0;

                      return (
                        <Grid item xs={12} md={6} key={`${propertyId}-${kpiKey}`}>
                          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: "16px", border: "1px solid #e0e0e0" }}>
                            {hasPredictions ? (
                              <PredictionBarChart kpi={kpiKey} data={kpiData.predictions} />
                            ) : (
                              <Box sx={{ height: 250, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                  {kpiKey}
                                </Typography>
                                <NoDataFound />
                              </Box>
                            )}

                            {kpiData.narration && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                  {kpiData.narration}
                                </Typography>
                              </>
                            )}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      <Grid container spacing={2}>
        {Object.keys(graphData).map((kpiKey) => {
          const kpiData = graphData[kpiKey];
          const hasPredictions = kpiData?.predictions?.length > 0;

          return (
            <Grid item xs={12} md={6} key={kpiKey}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: "16px",
                  border: "1px solid #e0e0e0",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.05)"
                }}
              >
                {hasPredictions ? (
                  <PredictionBarChart kpi={kpiKey} data={kpiData.predictions} />
                ) : (
                  <Box sx={{ height: 250, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', fontSize: '0.9rem', fontWeight: 'bold' }}>
                      {kpiKey}
                    </Typography>
                    <NoDataFound />
                  </Box>
                )}

                {kpiData.narration && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {kpiData.narration}
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const CustomTooltip = ({ active, payload, label, kpi }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isPrediction = data.is_prediction;
    const confidence = data.confidence_pct;

    const formattedLabel = dayjs(label).isValid()
      ? dayjs(label).format("MMM D, YYYY")
      : label;

    return (
      <Paper elevation={3} sx={{ p: 1.5, border: "1px solid #e0e0e0", fontSize: "0.75rem", minWidth: 200, zIndex: 100 }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 0.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          <span style={{ fontWeight: 'bold' }}>{formattedLabel}</span>
          <Box
            component="span"
            sx={{
              fontSize: '0.65rem',
              px: 0.8,
              py: 0.2,
              borderRadius: 1,
              whiteSpace: 'nowrap',
              bgcolor: isPrediction ? '#e3f2fd' : '#f5f5f5',
              color: isPrediction ? '#1976d2' : '#666',
              fontWeight: 'bold'
            }}
          >
            {isPrediction ? "FORECAST" : "ACTUAL"}
          </Box>
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Typography variant="body2" color="primary" fontWeight="bold">
          Value: {formatKpiValue(data.value, kpi)}
        </Typography>

        {isPrediction && confidence && (
          <Typography variant="caption" sx={{ color: '#1976d2', display: 'block', mt: 0.5, fontWeight: 600 }}>
            Confidence Score: {confidence}%
          </Typography>
        )}
      </Paper>
    );
  }
  return null;
};